import * as React from 'react';
import clsx from 'clsx';
import dechoesLogo from '@/assets/images/icons/dechoes.svg';
import authElement from '@/assets/images/elements/auth_element.svg';
import bigPinkBlur from '@/assets/images/elements/big_pink_blur.svg';
import BackgroundBubbles from '@/components/common/BackgroundBubbles';
import { AuthMobileHeader } from '@/components/layout/auth/AuthMobileHeader';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { NewPasswordForm } from './NewPasswordForm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';

type Step = 'email' | 'new-password';

interface ForgotPasswordProps {
  onSwitch?: () => void;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onSwitch }) => {
  const [step, setStep] = React.useState<Step>('email');
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const token = searchParams.get('token');

  // Automatically switch to new-password step if token is present
  React.useEffect(() => {
    if (token) {
      setStep('new-password');
    }
  }, [token]);

  const handleEmailSubmit = async (email: string) => {
    setIsLoading(true);
    try {
      const response = await api.auth.forgotPassword(email);

      // Logic to handle new backend response structure
      if (response.success) {
        addToast({
          title: 'Check your email',
          subtitle: response.message,
          type: 'success',
        });
      } else if (response.is_oauth_user) {
        // Specific UI feedback for OAuth users trying to reset password
        addToast({
          title: 'Social Login Required',
          subtitle: response.message,
          type: 'info',
        });
      } else {
        // Fallback for other logical failures (unlikely given spec, but safe)
        addToast({
          title: 'Request Failed',
          subtitle: response.message,
          type: 'error',
        });
      }
    } catch (error) {
      addToast({
        title: 'Request Failed',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (password: string) => {
    if (!token) {
      addToast({
        title: 'Error',
        subtitle: 'Missing reset token. Please request a new link.',
        type: 'error',
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.auth.resetPassword(token, password);
      addToast({
        title: 'Success',
        subtitle: response.message,
        type: 'success',
      });
      // Navigate back to login
      navigate('/login');
    } catch (error) {
      addToast({
        title: 'Reset Failed',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = () => {
    onSwitch?.();
  };

  return (
    <div className='flex size-full flex-col items-center overflow-hidden'>
      {/* Mobile header — matches login layout */}
      <div className='flex w-full shrink-0 items-center justify-between px-[15px] pb-3 pt-[15px] fold:hidden'>
        <AuthMobileHeader />
      </div>

      {/* Desktop header: Logo + tagline (fold+) */}
      <div className='relative z-20 hidden shrink-0 items-center gap-4 pt-10 fold:flex'>
        <img src={dechoesLogo} alt='d.echo' className='h-auto object-contain' />
        <span
          className={clsx(
            'font-grape font-normal leading-none tracking-normal',
            'text-30 text-brand-800'
          )}
        >
          your inner circle now includes the greatest
        </span>
      </div>

      {/* Content Switch */}
      <div className={clsx('relative flex w-full items-center justify-center p-4', 'fold:flex-1')}>
        {/* Background Elements (z-0) */}
        <div className='pointer-events-none absolute left-1/2 top-1/2 z-0 size-[600px] -translate-x-1/2 -translate-y-1/2'>
          {/* 1. Auth Element (Center) - HIDDEN ON MOBILE */}
          <img
            src={authElement}
            alt=''
            className='absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 select-none fold:block'
          />

          {/* 2. Pink Blur (On Top of Element) - HIDDEN ON MOBILE */}
          <img
            src={bigPinkBlur}
            alt=''
            className='absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 select-none fold:block'
          />

          {/* 3. White Bubbles (Constrained to this 600px box) */}
          <BackgroundBubbles count={80} variant='white' className='opacity-80 mix-blend-overlay' />
        </div>

        {/* Form Container (z-10) */}
        <div className='relative z-10 flex w-full justify-center'>
          {step === 'email' ? (
            <ForgotPasswordForm
              onSubmit={handleEmailSubmit}
              onRegister={handleSignUp}
              isLoading={isLoading}
            />
          ) : (
            <NewPasswordForm
              onSubmit={handlePasswordSubmit}
              onRegister={handleSignUp}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};
